import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { api, extractErr } from "@/portal/PortalAuth";
import { btn, ghost, inp } from "@/portal/PortalShell";
import { AirbnbConnect } from "@/portal/AirbnbConnect";

const slugify = (s) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function Drawer({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid}>
      <button aria-label="Close" className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative w-full lg:w-[680px] bg-white h-full overflow-y-auto shadow-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-xl font-light tracking-tight text-[#1C1917]">{title}</p>
          <button onClick={onClose} data-testid={`${testid}-close`} className="p-2 rounded-full hover:bg-[#F5F5F4]"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </aside>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block text-xs text-[#78716C]">{label}<div className="mt-1">{children}</div></label>;
}

/** + Add Listing: creates a draft or published listing; photos & availability continue in the listing editor. */
export function AddListingDrawer({ onClose, onCreated }) {
  const [props, setProps] = useState([]);
  const [fees, setFees] = useState(null);
  const [newProp, setNewProp] = useState(false);
  const [p, setP] = useState({ name: "", type: "furnished_room", address: "", city: "Austin" });
  const [f, setF] = useState({ property_id: "", name: "", slug: "", description: "", address: "", amenities: "", house_rules: "",
    max_guests: 2, bedrooms: "", bathrooms: "", sqft: "", monthly_rate: "", min_nights: 30, cleaning_fee: "", management_fee_pct: "" });
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/admin/booking/properties").then(({ data }) => setProps(Array.isArray(data) ? data : [])).catch(() => setProps([]));
    api.get("/admin/booking/payment-settings").then(({ data }) => setFees(data?.fees || null)).catch(() => null);
  }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const type = newProp ? p.type : props.find((x) => x.id === f.property_id)?.type || "furnished_room";
  const catFees = fees?.[type] || {};

  const save = async (status) => {
    if (!f.name.trim() || !f.slug || !Number(f.monthly_rate)) { toast.error("Title, room identifier and monthly rate are required."); return; }
    setBusy(status);
    try {
      let propertyId = f.property_id;
      if (newProp) {
        if (!p.name.trim()) throw new Error("Enter the new property name.");
        const { data } = await api.post("/admin/booking/properties", { ...p, slug: slugify(p.name) });
        propertyId = data.id;
      }
      if (!propertyId) throw new Error("Select or create a property.");
      const body = {
        property_id: propertyId, name: f.name.trim(), slug: f.slug, description: f.description, address: f.address,
        amenities: lines(f.amenities), house_rules: lines(f.house_rules), max_guests: Number(f.max_guests) || 1,
        monthly_rate: Number(f.monthly_rate), min_nights: Math.max(30, Number(f.min_nights) || 30), status,
        tier: type === "luxury_home" ? "Entire Home" : type === "furnished_private_home" ? "Entire Home" : "", active: true,
        bedrooms: f.bedrooms === "" ? null : Number(f.bedrooms),
        bathrooms: f.bathrooms === "" ? null : Number(f.bathrooms),
        sqft: f.sqft === "" ? null : Number(f.sqft),
        cleaning_fee: f.cleaning_fee === "" ? null : Number(f.cleaning_fee),
        management_fee_pct: f.management_fee_pct === "" ? null : Number(f.management_fee_pct),
      };
      const { data } = await api.post("/admin/booking/rooms", body);
      toast.success(status === "draft" ? "Draft saved. Add photos and availability next." : "Listing published. Add photos and availability next.");
      onCreated(data.id);
    } catch (e) { toast.error(e.message && !e.response ? e.message : extractErr(e)); } finally { setBusy(""); }
  };

  return (
    <Drawer title="Add listing" onClose={onClose} testid="add-listing-drawer">
      <div className="space-y-4">
        <div className="flex gap-2">
          <button onClick={() => setNewProp(false)} data-testid="add-listing-existing-prop" className={`${!newProp ? btn : ghost} text-xs`}>Existing property</button>
          <button onClick={() => setNewProp(true)} data-testid="add-listing-new-prop" className={`${newProp ? btn : ghost} text-xs`}>New property</button>
        </div>
        {!newProp ? (
          <F label="Property">
            <select value={f.property_id} onChange={(e) => set("property_id", e.target.value)} data-testid="add-listing-property" className={inp}>
              <option value="">Select a property…</option>
              {props.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.type === "luxury_home" ? "Luxury home" : x.type === "furnished_private_home" ? "Private home" : "Furnished rooms"}</option>)}
            </select>
          </F>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Property name"><input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="add-prop-name" className={inp} /></F>
            <F label="Property type">
              <select value={p.type} onChange={(e) => setP({ ...p, type: e.target.value })} data-testid="add-prop-type" className={inp}>
                <option value="furnished_room">Furnished room(s)</option><option value="furnished_private_home">Furnished private home (entire house)</option><option value="luxury_home">Luxury entire home</option>
              </select>
            </F>
            <F label="Property address"><input value={p.address} onChange={(e) => setP({ ...p, address: e.target.value })} data-testid="add-prop-address" className={inp} /></F>
            <F label="City"><input value={p.city} onChange={(e) => setP({ ...p, city: e.target.value })} data-testid="add-prop-city" className={inp} /></F>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Listing title"><input value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value, slug: x.slugEdited ? x.slug : slugify(e.target.value) }))} data-testid="add-listing-title" className={inp} /></F>
          <F label="Unique room identifier"><input value={f.slug} onChange={(e) => setF((x) => ({ ...x, slug: slugify(e.target.value), slugEdited: true }))} data-testid="add-listing-slug" className={inp} /></F>
        </div>
        <F label="Description"><textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="add-listing-description" className={inp + " h-auto py-2"} /></F>
        <F label="Listing address (unit / room location, optional)"><input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="add-listing-address" className={inp} /></F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Amenities (one per line)"><textarea rows={3} value={f.amenities} onChange={(e) => set("amenities", e.target.value)} data-testid="add-listing-amenities" className={inp + " h-auto py-2"} /></F>
          <F label="House rules (one per line)"><textarea rows={3} value={f.house_rules} onChange={(e) => set("house_rules", e.target.value)} data-testid="add-listing-rules" className={inp + " h-auto py-2"} /></F>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <F label="Bedrooms (whole homes)"><input type="number" min={0} value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="add-listing-bedrooms" className={inp} /></F>
          <F label="Bathrooms (whole homes)"><input type="number" min={0} step="0.5" value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} data-testid="add-listing-bathrooms" className={inp} /></F>
          <F label="Square footage (optional)"><input type="number" min={0} value={f.sqft} onChange={(e) => set("sqft", e.target.value)} data-testid="add-listing-sqft" className={inp} /></F>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <F label="Max guests"><input type="number" min={1} value={f.max_guests} onChange={(e) => set("max_guests", e.target.value)} data-testid="add-listing-guests" className={inp} /></F>
          <F label="Monthly rate ($)"><input type="number" min={0} value={f.monthly_rate} onChange={(e) => set("monthly_rate", e.target.value)} data-testid="add-listing-monthly" className={inp} /></F>
          <F label="Minimum stay (nights)"><input type="number" min={30} value={f.min_nights} onChange={(e) => set("min_nights", e.target.value)} data-testid="add-listing-min-nights" className={inp} /></F>
          <F label={`Cleaning fee ($, default ${catFees.cleaning_fee ?? "—"})`}><input type="number" min={0} placeholder={String(catFees.cleaning_fee ?? "")} value={f.cleaning_fee} onChange={(e) => set("cleaning_fee", e.target.value)} data-testid="add-listing-cleaning" className={inp} /></F>
          <F label={`Management fee (%, default ${catFees.management_fee_pct ?? "—"})`}><input type="number" min={0} max={100} placeholder={String(catFees.management_fee_pct ?? "")} value={f.management_fee_pct} onChange={(e) => set("management_fee_pct", e.target.value)} data-testid="add-listing-mgmt" className={inp} /></F>
        </div>
        <p className="text-xs text-[#78716C]">After saving, the listing editor opens so you can upload photos, choose the cover and set availability. Airbnb sync is never switched on automatically.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => save("draft")} disabled={!!busy} data-testid="add-listing-save-draft" className={ghost + " text-sm"}>{busy === "draft" && <Loader2 className="w-4 h-4 animate-spin" />} Save draft</button>
          <button onClick={() => save("published")} disabled={!!busy} data-testid="add-listing-publish" className={btn + " text-sm"}>{busy === "published" && <Loader2 className="w-4 h-4 animate-spin" />} Publish</button>
        </div>
      </div>
    </Drawer>
  );
}

/** + Add Airbnb Sync: choose a Masaya listing, then verify -> preview -> confirm. */
export function AddSyncDrawer({ onClose }) {
  const [rooms, setRooms] = useState([]);
  const [rid, setRid] = useState("");
  const load = () => api.get("/admin/booking/rooms").then(({ data }) => setRooms(Array.isArray(data) ? data : [])).catch((e) => toast.error(extractErr(e)));
  useEffect(() => { load(); }, []);
  const room = rooms.find((r) => r.id === rid);
  return (
    <Drawer title="Add Airbnb calendar sync" onClose={onClose} testid="add-sync-drawer">
      <F label="Masaya listing">
        <select value={rid} onChange={(e) => setRid(e.target.value)} data-testid="add-sync-room" className={inp}>
          <option value="">Select a listing…</option>
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}{r.ical_sync_enabled ? " · connected" : ""}{r.status === "draft" ? " · draft" : ""}</option>)}
        </select>
      </F>
      {room && (
        <div className="mt-4 text-sm">
          <p className="text-[#1C1917]">{room.name} <span className="text-xs text-[#78716C]">· {room.slug} · Airbnb listing {room.airbnb_listing_id || "not linked"}</span></p>
          <AirbnbConnect room={room} onChanged={load} />
        </div>
      )}
      <p className="mt-6 text-xs text-[#78716C]">Uses Airbnb's official calendar export (iCal). Imported dates are read-only blocks; Airbnb reservations, prices and payments are never changed.</p>
    </Drawer>
  );
}
